/**
 * @fileoverview Hydraulic Balancing Simulation
 * 
 * An interactive simulation demonstrating hydraulic balancing principles
 * for multi-room heat pump heating systems. This educational tool shows
 * how preset valve adjustments affect flow distribution and temperature
 * control across different floors and positions in a building.
 * 
 * Key Learning Objectives:
 * - Understanding hydraulic pressure distribution in multi-story buildings
 * - Importance of preset valve adjustment based on floor height
 * - Impact of radiator sizing on room temperature control
 * - Relationship between supply temperature, flow rate, and heat output
 * - Heat pump COP variation with operating conditions
 * 
 * Scientific Basis:
 * - Darcy-Weisbach equation for pipe pressure losses
 * - EN 442 radiator heat output calculation (Q = K × ΔT^n)
 * - Valve Kv flow coefficient relationships
 * - Carnot efficiency for heat pump COP estimation
 * - Thermal mass and time constant modeling for rooms
 * 
 * @module HydraulicBalancing
 * @author HeatCraftHP Team
 * @version 2.0.0
 * @license MIT
 */

import React, { useMemo, useCallback } from 'react';
import { Space } from 'antd';

// Wrapper component
import PagesWrapper from '../../../PagesWrapper';

// Local components
import {
  RoomUnit,
  RoomControls,
  AddRoomPlaceholder,
  HeatPumpUnit,
  OutdoorConditions,
  BuildingEdge,
  SimulationToolbar,
  SystemParametersPanel,
  RadiatorRatingOverlay,
  TemperatureRatingOverlay,
  HeatFluxOverlay,
  UValueRatingOverlay,
} from './components';

// Hooks
import {
  useSimulation,
  useVisualizationOverlays,
  useConfigurationIO,
  useRoomSelection,
} from './hooks';

// Constants
import {
  SVG_DIMENSIONS,
  BUILDING_CONFIG,
} from './constants';

// Utilities
import { canPlaceRoom, canDeleteRoom } from './utils';

// Styles
import styles from './HydraulicBalancing.module.css';

const { WIDTH, HEIGHT, ROOM_WIDTH, ROOM_HEIGHT, MARGIN_LEFT, GROUND_Y } = SVG_DIMENSIONS;

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * HydraulicBalancingSimulation - Main simulation component
 * 
 * Provides an interactive visualization of a multi-room heating system
 * with adjustable parameters for understanding hydraulic balancing.
 * 
 * Features:
 * - Visual building with up to 12 rooms (4 floors × 3 rooms)
 * - Real-time thermal simulation with animated heat transfer
 * - Adjustable system parameters (temperatures, pressures, valve settings)
 * - Multiple visualization overlays (heat flux, temperatures, R-values)
 * - Import/export configuration functionality
 * 
 * @component
 * @example
 * return <HydraulicBalancingSimulation />
 */
export function HydraulicBalancingSimulation() {
  // -------------------------------------------------------------------------
  // Hooks
  // -------------------------------------------------------------------------
  
  // Main simulation state and controls
  const simulation = useSimulation();
  
  const {
    // System parameters
    tOutdoor,
    setTOutdoor,
    tSupply,
    setTSupply,
    uRoomToRoom,
    setURoomToRoom,
    systemPressure,
    setSystemPressure,
    kvMax,
    setKvMax,
    kRad,
    setKRad,
    pipeLength,
    setPipeLength,
    pipeDiameter,
    setPipeDiameter,
    airChangeRate,
    setAirChangeRate,
    
    // Building state
    rooms,
    selectedRoom,
    setSelectedRoom,
    
    // Simulation control
    isRunning,
    setIsRunning,
    
    // Derived values
    cop,
    getMixedReturnTemp,
    getCombinedValveOpening,
    
    // Room manipulation
    updateSelectedRoom,
    addRoom,
    deleteRoom,
  } = simulation;
  
  // Visualization overlays
  const overlays = useVisualizationOverlays();
  const {
    showRadiatorRating,
    showTemperatureRating,
    showHeatFlux,
    showUValueRating,
    toggleOverlay,
  } = overlays;
  
  // Import/Export functionality
  const {
    isLoading,
    exportConfiguration,
    importConfiguration,
    contextHolder,
  } = useConfigurationIO(simulation);
  
  // Room selection utilities
  const {
    getValidRoomPositions,
    getSelectedRoom,
    selectedRoomNumber,
  } = useRoomSelection(rooms, selectedRoom, setSelectedRoom);

  // -------------------------------------------------------------------------
  // Memoized Values
  // -------------------------------------------------------------------------
  
  /** List of valid room positions for selection dropdown */
  const roomOptions = useMemo(() => 
    getValidRoomPositions().map(({ floor, col }) => [floor, col]),
    [getValidRoomPositions]
  );
  
  /** Currently selected room object */
  const selectedRoomData = useMemo(() => 
    getSelectedRoom(),
    [getSelectedRoom]
  );
  
  /** Total number of floors in building */
  const totalFloors = useMemo(() => 
    rooms.length,
    [rooms]
  );
  
  /** Check if there's any flow in the system */
  const hasFlow = useMemo(() => 
    getCombinedValveOpening() > 0,
    [getCombinedValveOpening]
  );
  
  /** Mixed return temperature */
  const mixedReturnTemp = useMemo(() => 
    getMixedReturnTemp(),
    [getMixedReturnTemp]
  );

  // -------------------------------------------------------------------------
  // Callbacks
  // -------------------------------------------------------------------------
  
  /** Toggle simulation running state */
  const handleToggleRun = useCallback(() => {
    setIsRunning(prev => !prev);
  }, [setIsRunning]);
  
  /** Handle room selection from SVG click */
  const handleRoomSelect = useCallback((floor, col) => {
    setSelectedRoom([floor, col]);
  }, [setSelectedRoom]);
  
  /** Check if room can be deleted */
  const checkCanDelete = useCallback((floor, col) => {
    return canDeleteRoom(rooms, floor, col);
  }, [rooms]);

  // -------------------------------------------------------------------------
  // Render Helpers
  // -------------------------------------------------------------------------
  
  /**
   * Renders all rooms in the building SVG
   */
  const renderRooms = () => {
    const elements = [];
    
    rooms.forEach((floor, floorIndex) => {
      floor.forEach((room, colIndex) => {
        const x = MARGIN_LEFT + ROOM_WIDTH * colIndex;
        const y = GROUND_Y - ROOM_HEIGHT * (floorIndex + 1);
        const roomNumber = floorIndex * BUILDING_CONFIG.MAX_ROOMS_PER_FLOOR + colIndex + 1;
        
        if (room) {
          elements.push(
            <RoomUnit
              key={`room-${floorIndex}-${colIndex}`}
              roomNumber={roomNumber}
              room={room}
              x={x}
              y={y}
              floorIndex={floorIndex}
              colIndex={colIndex}
              isSelected={selectedRoom[0] === floorIndex && selectedRoom[1] === colIndex}
              onSelect={handleRoomSelect}
            />
          );
        }
      });
    });
    
    return elements;
  };
  
  /**
   * Renders add room placeholders for valid positions
   */
  const renderAddPlaceholders = () => {
    const elements = [];
    
    // Check positions for potential new rooms
    for (let floor = 0; floor < BUILDING_CONFIG.MAX_FLOORS; floor++) {
      for (let col = 0; col < BUILDING_CONFIG.MAX_ROOMS_PER_FLOOR; col++) {
        if (!rooms[floor]?.[col] && canPlaceRoom(rooms, floor, col)) {
          const x = MARGIN_LEFT + ROOM_WIDTH * col;
          const y = GROUND_Y - ROOM_HEIGHT * (floor + 1);
          
          elements.push(
            <AddRoomPlaceholder
              key={`add-${floor}-${col}`}
              x={x}
              y={y}
              floorIndex={floor}
              colIndex={col}
              canAdd={true}
              onAdd={addRoom}
            />
          );
        }
      }
    }
    
    return elements;
  };
  
  /**
   * Renders the active visualization overlay
   */
  const renderOverlay = () => {
    if (showRadiatorRating) {
      return <RadiatorRatingOverlay rooms={rooms} tOutdoor={tOutdoor} tSupply={tSupply} />;
    }
    if (showTemperatureRating) {
      return <TemperatureRatingOverlay rooms={rooms} />;
    }
    if (showHeatFlux) {
      return <HeatFluxOverlay rooms={rooms} tOutdoor={tOutdoor} />;
    }
    if (showUValueRating) {
      return <UValueRatingOverlay rooms={rooms} />;
    }
    return null;
  };

  // -------------------------------------------------------------------------
  // Main Render
  // -------------------------------------------------------------------------
  
  return (
    <PagesWrapper>
      {/* Ant Design message context */}
      {contextHolder}
      
      <div className={styles.simulationContainer}>
          {/* Header Section */}
          <Space style={{ width: '100%'}} align="start" direction='vertical' size="large">
            <p className={styles.title}>Hydraulic Balancing Simulation</p>

            <SimulationToolbar
              isRunning={isRunning}
              onToggleRun={handleToggleRun}
              overlays={{
                showRadiatorRating,
                showTemperatureRating,
                showHeatFlux,
                showUValueRating,
              }}
              onToggleOverlay={toggleOverlay}
              onExport={exportConfiguration}
              onImport={importConfiguration}
              isLoading={isLoading}
            />
        </Space>
        
        {/* Main Content */}
        <div className={styles.mainContent}>
          {/* Building Visualization SVG */}
          <div className={styles.svgContainer}>
            <svg width={WIDTH} height={HEIGHT}>
              {/* Background grid (optional) */}
              <defs>
                <pattern
                  id="grid"
                  width="50"
                  height="50"
                  patternUnits="userSpaceOnUse"
                >
                  <path
                    d="M 50 0 L 0 0 0 50"
                    fill="none"
                    stroke="rgba(0,0,0,0.05)"
                    strokeWidth="1"
                  />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="white" />
              
              {/* Outdoor conditions display */}
              <OutdoorConditions temperature={tOutdoor} />
              
              {/* Heat pump unit */}
              <HeatPumpUnit
                isRunning={isRunning}
                hasFlow={hasFlow}
                systemPressure={systemPressure}
                tSupply={tSupply}
                tReturn={mixedReturnTemp}
                cop={cop}
              />
              
              {/* Add room placeholders (hidden when heat flux overlay is shown) */}
              {!showHeatFlux && renderAddPlaceholders()}
              
              {/* Building rooms */}
              {renderRooms()}
              
              {/* Building outline */}
              <BuildingEdge rooms={rooms} />
              
              {/* Active overlay */}
              {renderOverlay()}
            </svg>
          </div>
          
          {/* Controls Section */}
          <Space direction="vertical" size="large">
            {/* System Parameters */}
            <SystemParametersPanel
              params={{
                tSupply,
                tOutdoor,
                uRoomToRoom,
                systemPressure,
                kvMax,
                kRad,
                pipeLength,
                pipeDiameter,
                airChangeRate,
              }}
              setters={{
                setTSupply,
                setTOutdoor,
                setURoomToRoom,
                setSystemPressure,
                setKvMax,
                setKRad,
                setPipeLength,
                setPipeDiameter,
                setAirChangeRate,
              }}
            />
            
            {/* Room Controls */}
            {selectedRoomData && (
              <RoomControls
                room={selectedRoomData}
                floorIndex={selectedRoom[0]}
                colIndex={selectedRoom[1]}
                roomNumber={selectedRoomNumber}
                roomOptions={roomOptions}
                kRad={kRad}
                tSupply={tSupply}
                totalFloors={totalFloors}
                systemPressure={systemPressure}
                pipeDiameter={pipeDiameter}
                canDelete={checkCanDelete(selectedRoom[0], selectedRoom[1])}
                onSelectRoom={setSelectedRoom}
                onUpdateRoom={updateSelectedRoom}
                onDeleteRoom={deleteRoom}
              />
            )}
          </Space>
        </div>
      </div>
    </PagesWrapper>
  );
}

// Default export
export default HydraulicBalancingSimulation;
